import { call, put, select, takeLatest } from 'redux-saga/effects';
import { reviewApprovalActions } from 'app/pages/ReviewApproval/slice';
import { homeActions as actions } from '.';
import { actions as alertsNotificationActions } from 'app/components/AlertsAndNotification/store/slice';
import request from 'utils/request';
import { selectDateRange, selectMyStuffView } from './selectors';
import { formUrlParameters } from 'utils/helpers';
import { mapApiResponse } from 'utils/apiMapping';
import { ToastQueue } from '@adobe/react-spectrum';
import { MOMENT_TYPES } from 'utils/constants';

/**
 * Build the structured date payload for Price Changes and Offers /card PATCH.
 * Dates are sent as typed objects: { type: 'date'|'quarter'|'month', date|quarter|month: value }
 */
function formatPCOFields(fields: {
  title?: string;
  reportingSummary?: string;
  releaseDate?: string;
  releaseDateKey?: string;
  effectiveDate?: string;
  effectiveDateKey?: string;
  riskLevel?: string;
}) {
  const {
    title,
    reportingSummary,
    releaseDate,
    releaseDateKey,
    effectiveDate,
    effectiveDateKey,
    riskLevel
  } = fields;

  const result: Record<string, any> = {};

  if (title?.trim()) result.name = title;
  if (reportingSummary !== undefined) result.headline = reportingSummary;
  if (riskLevel?.trim()) result.riskLevel = riskLevel;

  const buildDateField = (value?: string, key?: string) => {
    if (!value?.trim() || !key) return undefined;
    if (key === 'Date') return { type: 'date', date: value };
    if (key === 'Quarter') return { type: 'quarter', quarter: value };
    if (key === 'Month') return { type: 'month', month: value };
    return undefined;
  };

  const custNotif = buildDateField(releaseDate, releaseDateKey);
  if (custNotif) result.customerNotificationDate = custNotif;

  const effective = buildDateField(effectiveDate, effectiveDateKey);
  if (effective) result.effectiveDate = effective;

  return result;
}

/** EOL stages: structured customerNotificationDate + effectiveDate (same shape as phased PCO). */
function formatEolFields(
  fields: Parameters<typeof formatPCOFields>[0]
): ReturnType<typeof formatPCOFields> {
  return formatPCOFields(fields);
}

function formatCardPatchFields(
  fields: Parameters<typeof formatMomentFields>[0],
  momentType: string,
  releasePhaseId?: string
) {
  if (momentType === MOMENT_TYPES.END_OF_LIFE && releasePhaseId) {
    return formatEolFields(fields);
  }
  return formatMomentFields(fields, momentType);
}

// Helper function to format moment fields for API
function formatMomentFields(
  fields: {
    title?: string;
    reportingSummary?: string;
    releaseDate?: string;
    releaseDateKey?: string;
    effectiveDate?: string;
    effectiveDateKey?: string;
    riskLevel?: string;
  },
  momentType: string
) {
  const {
    title,
    reportingSummary,
    releaseDate,
    releaseDateKey,
    effectiveDate,
    effectiveDateKey,
    riskLevel
  } = fields;
  // With/Without Marketing ---> {release, target}
  let keyMapEffectiveDates = {
    Date: 'targetStart',
    Quarter: 'targetQuarter',
    Month: 'targetMonth'
  };
  let keyMapTargetDates = {
    Date: 'releaseDate',
    Quarter: 'releaseQuarter',
    Month: 'releaseMonth'
  };

  // Offers and Promos / Marketing Moment ---> {target, effective}
  if (
    momentType === MOMENT_TYPES.PROMOS_OFFERS ||
    momentType === MOMENT_TYPES.MARKETING_MOMENT
  ) {
    keyMapTargetDates = {
      Date: 'targetStart',
      Quarter: 'targetQuarter',
      Month: 'targetMonth'
    };

    keyMapEffectiveDates = {
      Date: 'effectiveDate',
      Quarter: 'effectiveQuarter',
      Month: 'effectiveMonth'
    };
  }

  // Price Changes and Offers (LEGACY non-phased / pco:false) ---> same flat {target, effective}
  // shape as Offers/Promos/Marketing Moment. Phased PCO moments (pco:true) never reach this
  // function — they take the /card + formatPCOFields path higher up which sends the structured
  // {customerNotificationDate: {type,date}, effectiveDate: {type,date}} objects.
  //
  // The old mapping here wrote non-existent fields like "customerNotificationDateMonth" on
  // legacy pco:false moments, which MongoDB silently dropped — updates appeared to no-op.
  if (momentType === MOMENT_TYPES.PRICE_CHANGES_AND_OFFERS) {
    keyMapTargetDates = {
      Date: 'targetStart',
      Quarter: 'targetQuarter',
      Month: 'targetMonth'
    };

    keyMapEffectiveDates = {
      Date: 'effectiveDate',
      Quarter: 'effectiveQuarter',
      Month: 'effectiveMonth'
    };
  }
  const sendDates = effectiveDate?.trim() !== '' || releaseDate?.trim() !== '';

  return {
    name: title?.trim() !== '' ? title : undefined,
    // headline: reportingSummary?.trim() !== '' ? reportingSummary : undefined,
    headline: reportingSummary,
    dates: sendDates
      ? {
          [keyMapEffectiveDates[effectiveDateKey || '']]:
            effectiveDate?.trim() !== '' ? effectiveDate : undefined,
          [keyMapTargetDates[releaseDateKey || '']]:
            releaseDate?.trim() !== '' ? releaseDate : undefined
        }
      : undefined,
    riskLevel: riskLevel?.trim() !== '' ? riskLevel : undefined
  };
}

type ReleaseDataAction = ReturnType<typeof actions.loadReleaseData> & {
  meta?: { updatedMoment: { issueId: string; releasePhaseId: string } };
};

function* loadReleases(QueryParams: ReleaseDataAction) {
  try {
    const dateRange = yield select(selectDateRange);
    const startDate = dateRange.start;
    const endDate = dateRange.end;
    const params = QueryParams.payload;
    let additionalParams = '';

    if (params) {
      additionalParams = formUrlParameters(params);
    }

    // Built-in "My Stuff" view: same date range/filter pipeline, backend swaps
    // the dataset to cards where the logged-in user is Primary Contact.
    const isMyStuff = yield select(selectMyStuffView);
    const path = isMyStuff ? '/moments/my-stuff' : '/moments';

    const response = yield call(
      request,
      `${process.env.REACT_APP_API_V2_URL}${path}?startDate=${startDate}&endDate=${endDate}&${additionalParams}`
    );
    const mappedResponse = mapApiResponse(response);
    yield put(actions.releaseDataloaded(mappedResponse));
    if (QueryParams.meta?.updatedMoment) {
      yield put(
        actions.setUpdateSuccessAlert({
          message: 'Card updated successfully',
          ...QueryParams.meta.updatedMoment
        })
      );
    }
  } catch (e: any) {
    // Extract only serializable error information
    const errorInfo = {
      status: e.response?.status || 'unknown',
      statusText: e.response?.statusText || 'Network Error',
      message: e.message || 'An error occurred'
    };
    yield put(actions.loadReleaseDataError(errorInfo));
  }
}

function* loadProducts(QueryParams: any) {
  try {
    const response = yield call(
      request,
      `${process.env.REACT_APP_ROADMAP_URL}api/v2/moments`
    );

    // Transform the entire response at once
    const mappedResponse = mapApiResponse(response);

    yield put(actions.productsLoaded(mappedResponse));
  } catch (e: any) {
    // Extract only serializable error information
    const errorInfo = {
      status: e.response?.status || 'unknown',
      statusText: e.response?.statusText || 'Network Error',
      message: e.message || 'An error occurred'
    };
    yield put(actions.loadProductsError(errorInfo));
  }
}

// Other saga functions remain unchanged as they use different endpoints
function* loadProductGroups(QueryParams: any) {
  try {
    let url = `${process.env.REACT_APP_ROADMAP_URL}product-groups`;

    // Add customerAudiences parameter if audiences are provided
    if (
      QueryParams.payload &&
      QueryParams.payload.audiences &&
      QueryParams.payload.audiences.length > 0
    ) {
      const audiences = Array.isArray(QueryParams.payload.audiences)
        ? QueryParams.payload.audiences.join(',')
        : QueryParams.payload.audiences;
      url += `?customerAudiences=${encodeURIComponent(audiences)}`;
    }

    const response = yield call(request, url);
    yield put(actions.productGroupsLoaded(response));
  } catch (e: any) {
    // Extract only serializable error information
    const errorInfo = {
      status: e.response?.status || 'unknown',
      statusText: e.response?.statusText || 'Network Error',
      message: e.message || 'An error occurred'
    };
    yield put(actions.loadProductGroupsError(errorInfo));
  }
}

function* loadCategories(QueryParams: any) {
  try {
    const productGroups = Array.isArray(QueryParams.payload.productGroups)
      ? QueryParams.payload.productGroups.join(',')
      : QueryParams.payload.productGroups;

    const response = yield call(
      request,
      `${
        process.env.REACT_APP_ROADMAP_URL
      }categories?productGroups=${encodeURIComponent(productGroups)}`
    );
    yield put(actions.catgoriesLoaded(response));
  } catch (e: any) {
    // Extract only serializable error information
    const errorInfo = {
      status: e.response?.status || 'unknown',
      statusText: e.response?.statusText || 'Network Error',
      message: e.message || 'An error occurred'
    };
    yield put(actions.loadCategoriesError(errorInfo));
  }
}

function* loadFeatures(QueryParams: any) {
  try {
    const response = yield call(
      request,
      `${process.env.REACT_APP_ROADMAP_URL}features`
    );

    yield put(actions.featuresLoaded(response));
  } catch (e: any) {
    // Extract only serializable error information
    const errorInfo = {
      status: e.response?.status || 'unknown',
      statusText: e.response?.statusText || 'Network Error',
      message: e.message || 'An error occurred'
    };
    yield put(actions.loadFeaturesError(errorInfo));
  }
}

function* loadAudiences(QueryParams: any) {
  try {
    const response = yield call(
      request,
      `${process.env.REACT_APP_ROADMAP_URL}customer-audiences`
    );

    yield put(actions.audiencesLoaded(response));
  } catch (e: any) {
    // Extract only serializable error information
    const errorInfo = {
      status: e.response?.status || 'unknown',
      statusText: e.response?.statusText || 'Network Error',
      message: e.message || 'An error occurred'
    };
    yield put(actions.loadAudiencesError(errorInfo));
  }
}

// function* loadProductFilter(QueryParams: any) {
//   try {
//     const productGroups = Array.isArray(QueryParams.payload.productGroups)
//       ? QueryParams.payload.productGroups.join(',')
//       : QueryParams.payload.productGroups;

//     const categories = Array.isArray(QueryParams.payload.categories)
//       ? QueryParams.payload.categories.join(',')
//       : QueryParams.payload.categories;

//     const response = yield call(
//       request,
//       `${
//         process.env.REACT_APP_ROADMAP_URL
//       }products?productGroups=${encodeURIComponent(
//         productGroups
//       )}&categories=${encodeURIComponent(categories)}`
//     );

//     yield put(actions.productFilterLoaded(response));
//   } catch (e: any) {
//     // Extract only serializable error information
//     const errorInfo = {
//       status: e.response?.status || 'unknown',
//       statusText: e.response?.statusText || 'Network Error',
//       message: e.message || 'An error occurred'
//     };
//     yield put(actions.loadProductFilterError(errorInfo));
//   }
// }

function* loadHierarchyFilters(queryParams: any) {
  try {
    // Circuit breaker: Check if we should skip API call due to repeated failures
    const state = yield select();
    const hierarchyFilters = state.home.hierarchyFilters;

    const MAX_FAILURES = 3;
    const CIRCUIT_RESET_TIME = 5 * 60 * 1000; // 5 minutes

    // If circuit breaker is open, check if enough time has passed to retry
    if (hierarchyFilters.circuitBreakerOpen) {
      const timeSinceLastFailure = hierarchyFilters.lastFailureTime
        ? Date.now() - hierarchyFilters.lastFailureTime
        : Infinity;

      if (timeSinceLastFailure < CIRCUIT_RESET_TIME) {
        const minutesRemaining = Math.ceil(
          (CIRCUIT_RESET_TIME - timeSinceLastFailure) / 60000
        );
        // Don't throw error, just return silently to avoid triggering error handling
        return;
      } else {
        // Enough time has passed, auto-reset circuit breaker
        // The reset will happen in the success handler
      }
    }

    const url = `${process.env.REACT_APP_API_V2_URL}/product-hierarchy-filters`;

    // Prepare request body with filter parameters
    let requestBody: {
      customerAudiences?: string[];
      productGroups?: string[];
      categories?: string[];
      products?: string[];
    } = {};
    let hasFilters = false;

    if (queryParams.payload) {
      const { customerAudiences, productGroups, categories, products } =
        queryParams.payload;

      if (customerAudiences && customerAudiences.length > 0) {
        requestBody.customerAudiences = Array.isArray(customerAudiences)
          ? customerAudiences
          : [customerAudiences];
        hasFilters = true;
      }
      if (productGroups && productGroups.length > 0) {
        requestBody.productGroups = Array.isArray(productGroups)
          ? productGroups
          : [productGroups];
        hasFilters = true;
      }
      if (categories && categories.length > 0) {
        requestBody.categories = Array.isArray(categories)
          ? categories
          : [categories];
        hasFilters = true;
      }
      if (products && products.length > 0) {
        requestBody.products = Array.isArray(products) ? products : [products];
        hasFilters = true;
      }
    }

    // Use POST method with request body
    const response = yield call(request, url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(requestBody)
    });

    // If this is a filtered request (has filters), update valid options
    if (hasFilters) {
      // Ensure surfaces exists in response shape even if API omits it
      const withSurfaces = {
        ...response,
        surfaces: Array.isArray((response as any)?.surfaces)
          ? (response as any).surfaces
          : []
      };
      yield put(actions.updateValidOptions(withSurfaces));
    } else {
      // Initial load, set all data
      const withSurfaces = {
        ...response,
        surfaces: Array.isArray((response as any)?.surfaces)
          ? (response as any).surfaces
          : []
      };
      yield put(actions.hierarchyFiltersLoaded(withSurfaces));
    }
  } catch (e: any) {
    // Extract only serializable error information
    const errorInfo = {
      status: e.response?.status || 'unknown',
      statusText: e.response?.statusText || 'Network Error',
      message: e.message || 'An error occurred'
    };
    yield put(actions.loadHierarchyFiltersError(errorInfo));
  }
}

function* updateMomentDetails(
  queryParams: ReturnType<typeof actions.updateMomentDetails>
) {
  try {
    const { issueId, momentType, fields, releasePhaseId, refreshSource } =
      queryParams.payload;

    let response;

    if (
      (momentType === MOMENT_TYPES.PRICE_CHANGES_AND_OFFERS ||
        momentType === MOMENT_TYPES.PROMOS_OFFERS) &&
      releasePhaseId
    ) {
      // Phased offer/price-change types: /card with structured date field objects
      // { momentType, releasePhaseId, fields: { riskLevel, customerNotificationDate: { type, date }, effectiveDate: { type, date } } }
      const pcoFields = formatPCOFields(fields);
      const url = `${process.env.REACT_APP_API_V2_URL}/moments/${issueId}/card`;
      response = yield call(
        request,
        url,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            momentType,
            releasePhaseId,
            fields: pcoFields
          })
        },
        { showCustomError: true }
      );
    } else {
      const formattedFields = formatCardPatchFields(
        fields,
        momentType,
        releasePhaseId
      );
      const url = `${process.env.REACT_APP_API_V2_URL}/moments/${issueId}/card`;
      response = yield call(
        request,
        url,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            momentType,
            releasePhaseId,
            fields: formattedFields
          })
        },
        { showCustomError: true }
      );
    }

    if (response.status !== 200) {
      yield put(actions.releaseDataloaded([]));
      // ✅ CHANGED: Use silent refresh to avoid screen freeze
      yield put(actions.loadReleaseDataSilent({}));
      // ToastQueue.negative(response.message, {
      //   timeout: 5000
      // });
      throw new Error(response.message);
      // throw new Error('Failed to update moment details');
    }

    ToastQueue.positive('Card updated successfully', {
      timeout: 3000
    });

    if (refreshSource === 'reviewApproval') {
      yield put(reviewApprovalActions.quickEditSaved());
    }

    // The refresh worker owns highlighting; a failed/cancelled refresh cannot
    // be completed by a different timeline request.
    yield put({
      ...actions.loadReleaseDataSilent({}),
      meta:
        refreshSource === 'reviewApproval'
          ? undefined
          : { updatedMoment: { issueId, releasePhaseId: releasePhaseId || '' } }
    });
    yield put(alertsNotificationActions.loadAlertsAndNotifications());
  } catch (e: any) {
    console.error('Error updating moment title:', e);
    ToastQueue.negative(e.message, {
      timeout: 5000
    });
  }
}

/**
 * Load Must Win filter options from JSON (for Left sidebar Must Wins list only)
 */
function* loadMustWinFilterOptions() {
  try {
    const response = yield call(fetch, '/mustWinFilters.json');
    if (!response.ok) {
      throw new Error(
        `Failed to fetch mustWinFilters.json: ${response.statusText}`
      );
    }
    const data = yield response.json();
    yield put(actions.mustWinFilterOptionsLoaded(data.filters || []));
  } catch (err) {
    console.error('Error loading Must Win filter options:', err);
    yield put(actions.loadMustWinFilterOptionsError(err));
  }
}

export function* homeSaga() {
  yield takeLatest(actions.loadReleaseData.type, loadReleases);
  // Silent refresh uses the same saga but with different loading state
  yield takeLatest(actions.loadReleaseDataSilent.type, loadReleases);
  yield takeLatest(actions.loadProductGroups.type, loadProductGroups);
  yield takeLatest(actions.loadProducts.type, loadProducts);
  yield takeLatest(actions.loadCategories.type, loadCategories);
  yield takeLatest(actions.loadFeatures.type, loadFeatures);
  yield takeLatest(actions.loadAudiences.type, loadAudiences);
  // yield takeLatest(actions.loadProductFilter.type, loadProductFilter);
  yield takeLatest(actions.loadHierarchyFilters.type, loadHierarchyFilters);
  yield takeLatest(actions.updateMomentDetails.type, updateMomentDetails);
  yield takeLatest(
    actions.loadMustWinFilterOptions.type,
    loadMustWinFilterOptions
  );
}
